/**
 * Note-subscription data layer — TanStack Query hooks over the gated per-node
 * note-subscription API
 * (`/api/teams/{teamId}/tasks/nodes/{nodeId}/note-subscription`). This is the
 * ONLY place the opt-in toggle talks to the server; the rendering component
 * (the drawer's "Notify me of notes on this task" toggle) consumes these hooks
 * and never calls `apiFetch` directly — the same query ⟂ view split the rest
 * of taskmap keeps (§A7), mirroring `useNodeNotes` / `useReminders`.
 *
 * Issue #907 (note-driven notifications). A subscription is the CALLER's own,
 * per-node opt-in to be notified when a note is added to that task. It is the
 * member's choice on their own terms — @mentions always notify regardless, so
 * this is purely an *additional* opt-in for tasks they want to follow. GET
 * hydrates the current state (so the toggle renders correct on open); PUT
 * subscribes, DELETE unsubscribes — both return the resolved `{subscribed}`.
 *
 * Keyed `["task-note-subscription", teamId, nodeId]`; both mutations invalidate
 * that key so the toggle re-derives from one fresh source of truth on success.
 *
 * Gating: only *consumed* behind `task_notes` (off by default, §A10); the
 * backend enforces the gate too (404-dark when off). The GET is `enabled` only
 * while the surface that hosts the toggle is open (both ids present), so opening
 * a drawer fires no subscription request until the toggle is shown; it degrades
 * calmly on a 404 (`retry: false`, treated as not-subscribed) so an
 * undeployed / ungated endpoint never crashes the drawer.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema
 * types — no scheduler coupling, no rendering.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

type NoteSubscriptionResponse = components["schemas"]["NoteSubscriptionResponse"];

/** Cache key for one node's note subscription. Exported for tests + invalidation. */
export const TASK_NOTE_SUBSCRIPTION_KEY = (teamId: string, nodeId: string): readonly string[] => [
  "task-note-subscription",
  teamId,
  nodeId,
];

const STALE_TIME_MS = 30_000;

function subscriptionUrl(teamId: string, nodeId: string): string {
  return `/api/teams/${teamId}/tasks/nodes/${nodeId}/note-subscription`;
}

export interface UseNoteSubscriptionResult {
  /** Whether the caller is currently subscribed to this node's note feed. */
  readonly subscribed: boolean;
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed / ungated 404). */
  readonly isError: boolean;
}

/**
 * Fetch (hydrate) the caller's subscription state for ONE node. `enabled` only
 * when both ids are present (i.e. the toggle is actually shown) so we never
 * fire a request for a closed drawer / hidden toggle. Degrades to "not
 * subscribed" on a non-boolean / errored body so the toggle never has to
 * null-check.
 */
export function useNoteSubscription(
  teamId: string | undefined,
  nodeId: string | undefined,
): UseNoteSubscriptionResult {
  const query = useQuery({
    queryKey: TASK_NOTE_SUBSCRIPTION_KEY(teamId ?? "", nodeId ?? ""),
    enabled: Boolean(teamId) && Boolean(nodeId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () =>
      apiFetch<NoteSubscriptionResponse>(subscriptionUrl(teamId as string, nodeId as string)),
  });
  return {
    subscribed: query.data?.subscribed === true,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Subscribe (PUT) or unsubscribe (DELETE) the caller from a node's note feed,
 * driven by the toggle. Invalidates the node's subscription key so the toggle
 * reflects the resolved server state on success. Both verbs return the
 * `{subscribed}` shape; idempotent server-side (subscribing twice / removing a
 * non-subscription is fine), so a rapid double-toggle can't break it.
 */
export function useSetNoteSubscription(teamId: string | undefined, nodeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscribe: boolean) =>
      apiFetch<NoteSubscriptionResponse>(subscriptionUrl(teamId as string, nodeId as string), {
        method: subscribe ? "PUT" : "DELETE",
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: TASK_NOTE_SUBSCRIPTION_KEY(teamId ?? "", nodeId ?? ""),
      }),
  });
}
